import { useState, useEffect } from 'react';
import { setProductReaction, getImageUrl } from '../utils/api';
import CampaignBadge from './CampaignBadge';
import CampaignCountdown from './CampaignCountdown';

// Countdown helper
const getTimeRemaining = (endTime) => {
    if (!endTime) return null;
    const total = endTime - new Date();
    if (total <= 0) return null;
    
    const hours = Math.floor(total / (1000 * 60 * 60));
    const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) return null; // Don't show countdown if more than 24 hours
    if (hours > 0) return `${hours}s ${minutes}dk kaldı`;
    return `${minutes}dk kaldı`;
};

// Campaign Countdown Component removed - using imported component

// Dietary tag icon definitions
const DIETARY_ICONS = {
    is_vegan: { icon: '🌱', label: 'Vegan', color: 'bg-green-500' },
    is_vegetarian: { icon: '🥬', label: 'Vejetaryen', color: 'bg-green-400' },
    is_gluten_free: { icon: '🌾', label: 'Glutensiz', color: 'bg-yellow-500' },
    contains_gluten: { icon: '⚠️', label: 'Gluten İçerir', color: 'bg-amber-500' },
    is_spicy: { icon: '🌶️', label: 'Acı', color: 'bg-red-500' },
    is_sweet: { icon: '🍯', label: 'Tatlı', color: 'bg-pink-500' },
    is_new: { icon: '✨', label: 'Yeni', color: 'bg-blue-500' },
    is_popular: { icon: '🔥', label: 'Popüler', color: 'bg-orange-500' },
    is_chef_recommended: { icon: '👨‍🍳', label: 'Şef Önerisi', color: 'bg-purple-500' }
};

const ProductCard = ({ product, onLike, onClick, settings = {}, isSettingEnabled = () => true, compact = false }) => {
    const [reaction, setReaction] = useState(product.reaction || null);
    const [likeCount, setLikeCount] = useState(product.like_count || 0);
    const [dislikeCount, setDislikeCount] = useState(product.dislike_count || 0);
    const [loading, setLoading] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    
    // Check if this is a Nox's Choice (winner) product
    const isNoxChoice = product.is_nox_choice || product.vote?.winner_product_id === product.id;

    // Initialize from product.liked for backward compatibility
    useEffect(() => {
        if (product.liked && !product.reaction) {
            setReaction('like');
        }
    }, [product.liked, product.reaction]);

    const handleReaction = async (e, type) => {
        e.stopPropagation();
        
        if (loading) return;

        setLoading(true);
        try {
            const result = await setProductReaction(product.id, type);
            setReaction(result.data.reaction);
            setLikeCount(result.data.like_count);
            setDislikeCount(result.data.dislike_count);
            if (onLike) {
                onLike(product.id, result.data.reaction);
            }
        } catch (error) {
            console.error('Reaction error:', error);
        } finally {
            setLoading(false);
        }
    };

    const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];

    // Calculate price with discount
    let displayPrice = parseFloat(product.price) || 0;
    let originalPrice = null;
    let discountPercent = null;
    let hasActiveDiscount = false;
    let discountEndTime = null;
    let isCombinationCampaign = false;

    // Check product-level discount first
    if (product.is_discount_active && product.discount_price) {
        originalPrice = displayPrice;
        displayPrice = parseFloat(product.discount_price);
        discountPercent = product.discount_percentage;
        hasActiveDiscount = true;
        
        // Calculate end time for countdown (use Turkey timezone UTC+3)
        if (product.discount_end_date || product.discount_end_time) {
            const now = new Date();
            const turkeyOffset = 3 * 60;
            const localOffset = now.getTimezoneOffset();
            const turkeyTime = new Date(now.getTime() + (turkeyOffset + localOffset) * 60 * 1000);
            const todayTurkey = `${turkeyTime.getFullYear()}-${String(turkeyTime.getMonth() + 1).padStart(2, '0')}-${String(turkeyTime.getDate()).padStart(2, '0')}`;
            
            const endDate = product.discount_end_date ? product.discount_end_date.split('T')[0] : todayTurkey;
            const endTime = product.discount_end_time ? product.discount_end_time.substring(0, 5) : '23:59';
            
            discountEndTime = new Date(`${endDate}T${endTime}:00+03:00`);
        }
    }
    // Then check campaign prices (new structure from backend)
    else if (product.campaign_prices && product.campaign_prices.length > 0) {
        // Use the first applicable campaign price
        const campaignPrice = product.campaign_prices[0];
        
        // Check for combination campaign first (case-insensitive)
        const isCombination = campaignPrice.campaign_type && 
            (campaignPrice.campaign_type.toLowerCase() === 'combination' || 
             campaignPrice.campaign_type.toLowerCase() === 'combo');
        
        if (isCombination && campaignPrice.combo_price) {
            // For combination campaigns, don't show product-level discount
            // Combination campaigns are shown in campaign info section, not as product discount
            isCombinationCampaign = true;
            // Keep original product price, don't modify displayPrice
            // hasActiveDiscount = false; // Don't show discount badge for combination campaigns
            if (campaignPrice.end_date) {
                discountEndTime = new Date(campaignPrice.end_date);
            }
        } else if (campaignPrice.campaign_type === 'discount' && campaignPrice.discounted_price) {
            originalPrice = parseFloat(campaignPrice.original_price) || displayPrice;
            displayPrice = parseFloat(campaignPrice.discounted_price) || displayPrice;
            const rawDiscount = campaignPrice.discount_percentage || 
                (campaignPrice.discount_type === 'percentage' ? campaignPrice.discount_value : null);
            discountPercent = rawDiscount ? Math.round(parseFloat(rawDiscount)) : null;
            hasActiveDiscount = true;
            
            // Calculate end time from campaign
            if (campaignPrice.end_date) {
                discountEndTime = new Date(campaignPrice.end_date);
            }
        } else if (campaignPrice.campaign_type === 'cross_product' || campaignPrice.campaign_type === 'cross_category') {
            if (campaignPrice.discounted_price) {
                originalPrice = parseFloat(campaignPrice.original_price) || displayPrice;
                displayPrice = parseFloat(campaignPrice.discounted_price) || displayPrice;
                const rawDiscount = campaignPrice.discount_type === 'percentage' ? campaignPrice.discount_value : null;
                discountPercent = rawDiscount ? Math.round(parseFloat(rawDiscount)) : null;
                hasActiveDiscount = true;
                
                if (campaignPrice.end_date) {
                    discountEndTime = new Date(campaignPrice.end_date);
                }
            }
        }
    }
    // Fallback to old campaign structure
    else if (product.campaigns && product.campaigns.length > 0) {
        const campaign = product.campaigns[0];
        if (campaign.campaign_type === 'discount' || campaign.type === 'discount') {
            const discountType = campaign.discount_type || campaign.discountType;
            const discountValue = campaign.discount_value || campaign.discountValue;
            if (discountType === 'percentage') {
                discountPercent = discountValue ? Math.round(parseFloat(discountValue)) : null;
                displayPrice = displayPrice * (1 - discountValue / 100);
            } else if (discountType === 'fixed_amount') {
                displayPrice = Math.max(0, displayPrice - discountValue);
            }
            originalPrice = parseFloat(product.price) || 0;
            hasActiveDiscount = true;
            
            if (campaign.end_date) {
                discountEndTime = new Date(campaign.end_date);
            }
        }
    }
    
    // Update countdown timer
    useEffect(() => {
        if (!discountEndTime || !hasActiveDiscount) {
            setTimeRemaining(null);
            return;
        }
        
        const updateTimer = () => {
            setTimeRemaining(getTimeRemaining(discountEndTime));
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 60000);
        
        return () => clearInterval(interval);
    }, [product.discount_end_date, product.discount_end_time, hasActiveDiscount, discountEndTime]);

    // Compact list view
    if (compact) {
        return (
            <div
                onClick={() => onClick && onClick(product)}
                className="flex items-center gap-3 p-3 rounded-lg hover:shadow-md transition cursor-pointer bg-white border border-gray-200"
            >
                {/* Small Image */}
                <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {primaryImage ? (
                        <img
                            src={getImageUrl(primaryImage.image_url)}
                            alt={product.name}
                            width={80}
                            height={80}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                            {settings?.default_product_image ? (
                                <img
                                    src={getImageUrl(settings.default_product_image)}
                                    alt={product.name}
                                    className="w-full h-full object-cover opacity-50"
                                    loading="lazy"
                                    decoding="async"
                                />
                            ) : (
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            )}
                        </div>
                    )}
                    {/* Campaign Badge on small image */}
                    {hasActiveDiscount && discountPercent && (
                        <div className="absolute top-0 right-0">
                            <span 
                                className="px-1.5 py-0.5 text-xs font-bold rounded shadow-lg"
                                style={{ 
                                    backgroundColor: 'var(--discount-badge-bg-color, #dc2626)',
                                    color: 'var(--discount-badge-text-color, #ffffff)'
                                }}
                            >
                                %{Math.round(discountPercent)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold mb-1 truncate" style={{ color: 'var(--product-title-color, #111827)', fontFamily: 'var(--font-product-title)' }}>
                        {product.name}
                    </h3>
                    {product.description && isSettingEnabled('show_descriptions', true) && (
                        <p className="text-xs mb-2 line-clamp-1" style={{ color: 'var(--product-description-color, #6b7280)', fontFamily: 'var(--font-product-description)' }}>
                            {product.description}
                        </p>
                    )}
                    
                    {/* Price */}
                    {isSettingEnabled('show_prices', true) && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Don't show discount prices for combination campaigns - show only product price */}
                            {!isCombinationCampaign && originalPrice && typeof originalPrice === 'number' && !isNaN(originalPrice) && (
                                <span className="text-xs line-through font-medium" style={{ color: '#dc2626' }}>
                                    {originalPrice.toFixed(2)} ₺
                                </span>
                            )}
                            {typeof displayPrice === 'number' && !isNaN(displayPrice) && (
                                <span 
                                    className="text-base font-bold"
                                    style={{ color: (!isCombinationCampaign && hasActiveDiscount) ? 'var(--discount-price-color, #dc2626)' : 'var(--product-price-color, #16a34a)' }}
                                >
                                    {displayPrice.toFixed(2)} ₺
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Regular card view
    return (
        <div
            onClick={() => onClick && onClick(product)}
            className={`rounded-lg overflow-hidden hover:shadow-lg transition cursor-pointer ${
                isNoxChoice 
                    ? 'bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-[2px]' 
                    : 'shadow-md'
            }`}
            style={{ 
                backgroundColor: isNoxChoice ? undefined : 'var(--product-bg-color, #ffffff)',
                borderColor: 'var(--product-border-color, #e5e7eb)'
            }}
        >
            <div className={isNoxChoice ? 'rounded-lg overflow-hidden' : ''} style={{ backgroundColor: isNoxChoice ? 'var(--product-bg-color, #ffffff)' : undefined }}>
            {/* Nox's Choice Badge */}
            {isNoxChoice && (
                <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 flex items-center gap-2">
                    <span className="text-base">🏆</span>
                    <span className="text-xs font-bold text-amber-700">Sizin Seçiminiz</span>
                </div>
            )}
            
            {/* Product Image */}
            <div className="relative h-48 overflow-hidden bg-gray-100">
                {primaryImage ? (
                    <>
                        {/* Loading Placeholder */}
                        {imageLoading && (
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex items-center justify-center">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                        {/* Actual Image */}
                        <img
                            src={getImageUrl(primaryImage.image_url)}
                            alt={product.name}
                            width={400}
                            height={192}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                            loading="lazy"
                            decoding="async"
                            onLoad={() => setImageLoading(false)}
                            onError={() => {
                                setImageLoading(false);
                                setImageError(true);
                            }}
                        />
                        {/* Error Placeholder */}
                        {imageError && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </>
                ) : (
                    // Placeholder image from settings or default
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                        {settings?.default_product_image ? (
                            <img
                                src={getImageUrl(settings.default_product_image)}
                                alt={product.name}
                                className="w-full h-full object-cover opacity-50"
                                loading="lazy"
                                decoding="async"
                            />
                        ) : (
                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        )}
                    </div>
                )}
                    
                    {/* Dietary Icons on Image */}
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                        {Object.keys(DIETARY_ICONS).map(key => {
                            if (product[key]) {
                                const tag = DIETARY_ICONS[key];
                                return (
                                    <span 
                                        key={key}
                                        className={`w-6 h-6 rounded-full ${tag.color} flex items-center justify-center text-xs shadow-md`}
                                        title={tag.label}
                                    >
                                        {tag.icon}
                                    </span>
                                );
                            }
                            return null;
                        })}
                    </div>
                    
                {/* Discount Badge - Show for all active discounts (including combination campaigns) */}
                {hasActiveDiscount && discountPercent && (
                    <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                        <span 
                            className="px-2 py-1 text-xs font-bold rounded-lg shadow-lg"
                            style={{ 
                                backgroundColor: 'var(--discount-badge-bg-color, #dc2626)',
                                color: 'var(--discount-badge-text-color, #ffffff)'
                            }}
                        >
                            %{Math.round(discountPercent)} İNDİRİM
                        </span>
                        {timeRemaining && (
                            <span className="px-2 py-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1">
                                <span className="relative">⏰</span>
                                <span>{timeRemaining}</span>
                            </span>
                        )}
                    </div>
                )}
                
                {/* Campaign Badge (only if no product discount and no discount badge shown) */}
                {!product.is_discount_active && !hasActiveDiscount && (
                    <>
                        {product.campaign_prices && product.campaign_prices.length > 0 && (
                            <div className="absolute top-2 right-2">
                                {(() => {
                                    console.log('🔍 ProductCard - Rendering CampaignBadge:', {
                                        productId: product.id,
                                        productName: product.name,
                                        campaignPricesLength: product.campaign_prices.length,
                                        firstCampaignPrice: product.campaign_prices[0],
                                        campaignsLength: product.campaigns?.length || 0,
                                        firstCampaign: product.campaigns?.[0]
                                    });
                                    return (
                                        <CampaignBadge 
                                            campaign={product.campaigns?.[0]} 
                                            campaignPrice={product.campaign_prices[0]} 
                                            product={product}
                                        />
                                    );
                                })()}
                            </div>
                        )}
                        {!product.campaign_prices && product.campaigns && product.campaigns.length > 0 && (
                        <div className="absolute top-2 right-2">
                            {(() => {
                                console.log('🔍 ProductCard - Rendering CampaignBadge (fallback):', {
                                    productId: product.id,
                                    productName: product.name,
                                    campaignsLength: product.campaigns.length,
                                    firstCampaign: product.campaigns[0]
                                });
                                return (
                                    <CampaignBadge 
                                        campaign={product.campaigns[0]} 
                                        product={product}
                                    />
                                );
                            })()}
                        </div>
                        )}
                    </>
                    )}
                </div>

            {/* Product Info */}
            <div className="p-4">
                <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--product-title-color, #111827)', fontFamily: 'var(--font-product-title)' }}>{product.name}</h3>
                
                {/* Dietary Tags Row */}
                <div className="flex flex-wrap items-center gap-1 mb-2">
                    {Object.keys(DIETARY_ICONS).map(key => {
                        if (product[key]) {
                            const tag = DIETARY_ICONS[key];
                            return (
                                <span 
                                    key={key}
                                    className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 flex items-center gap-0.5"
                                    title={tag.label}
                                >
                                    <span>{tag.icon}</span>
                                </span>
                            );
                        }
                        return null;
                    })}
                    {product.calories && isSettingEnabled('show_calories', false) && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
                            {product.calories} kcal
                        </span>
                    )}
                    {product.preparation_time && isSettingEnabled('show_preparation_time', true) && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            ⏱️ {product.preparation_time} dk
                        </span>
                    )}
                </div>
                
                {product.description && isSettingEnabled('show_descriptions', true) && (
                    <p className="text-sm mb-3 line-clamp-2" style={{ color: 'var(--product-description-color, #6b7280)', fontFamily: 'var(--font-product-description)' }}>{product.description}</p>
                )}

                {/* Price */}
                {isSettingEnabled('show_prices', true) && (
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Don't show discount prices for combination campaigns - show only product price */}
                        {!isCombinationCampaign && originalPrice && typeof originalPrice === 'number' && !isNaN(originalPrice) && (
                            <span className="text-sm line-through font-medium" style={{ color: '#dc2626' }}>
                                {originalPrice.toFixed(2)} ₺
                            </span>
                        )}
                        {typeof displayPrice === 'number' && !isNaN(displayPrice) && (
                            <span 
                                className="text-xl font-bold"
                                style={{ color: (!isCombinationCampaign && hasActiveDiscount) ? 'var(--discount-price-color, #dc2626)' : 'var(--product-price-color, #16a34a)' }}
                            >
                                {displayPrice.toFixed(2)} ₺
                            </span>
                        )}
                        {!isCombinationCampaign && discountPercent && (
                            <span 
                                className="px-2 py-0.5 text-xs font-bold rounded-full"
                                style={{
                                    backgroundColor: 'var(--discount-badge-bg-color, #dc2626)',
                                    color: 'var(--discount-badge-text-color, #ffffff)'
                                }}
                            >
                                %{Math.round(discountPercent)} İNDİRİM
                            </span>
                        )}
                    </div>
                    {/* Countdown - visible when no image */}
                    {!primaryImage && timeRemaining && (
                        <span className="px-2 py-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold rounded-lg shadow animate-pulse shrink-0">
                            ⏰ {timeRemaining}
                        </span>
                    )}
                </div>
                )}

                {/* Campaign Info */}
                {(product.campaign_prices && product.campaign_prices.length > 0) || (product.campaigns && product.campaigns.length > 0) ? (
                    <div className="mb-3 space-y-2">
                        {/* Show campaign titles with price emphasis */}
                        {product.campaigns && product.campaigns.map((campaign) => {
                            // Find matching price info if available
                            const priceInfo = product.campaign_prices?.find(cp => cp.campaign_id === campaign.id);
                            let priceDisplay = null;
                            
                            if (campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') {
                                const comboPrice = campaign.combo_price || priceInfo?.combo_price;
                                const totalOriginalPrice = priceInfo?.total_original_price;
                                
                                if (comboPrice) {
                                    if (totalOriginalPrice && totalOriginalPrice > comboPrice) {
                                        // Show crossed out original price and combo price
                                        priceDisplay = (
                                            <span className="flex items-center gap-2">
                                                <span className="line-through text-gray-400 text-sm">
                                                    {parseFloat(totalOriginalPrice).toFixed(0)}₺
                                                </span>
                                                <span className="font-bold text-red-600">
                                                    {parseFloat(comboPrice).toFixed(0)}₺
                                                </span>
                                            </span>
                                        );
                                    } else {
                                        priceDisplay = `${parseFloat(comboPrice).toFixed(0)}₺`;
                                    }
                                }
                            } else if (campaign.campaign_type === 'quantity_discount') {
                                // For quantity discount, find rules for this product and show prices
                                console.log('🔍 Quantity discount campaign:', {
                                    campaignId: campaign.id,
                                    campaignTitle: campaign.title,
                                    quantity_rules: campaign.quantity_rules,
                                    quantity_discount_type: campaign.quantity_discount_type,
                                    productId: product.id
                                });
                                
                                if (campaign.quantity_rules && campaign.quantity_rules.length > 0) {
                                    // Find rules that match this product
                                    const productRules = campaign.quantity_rules.filter(rule => 
                                        rule.product_id === product.id
                                    );
                                    
                                    console.log('🔍 Product rules for product', product.id, ':', productRules);
                                    
                                    if (productRules.length > 0) {
                                        // Get the first/best rule for this product
                                        const rule = productRules[0];
                                        
                                        console.log('🔍 Rule details:', {
                                            rule,
                                            quantity_discount_type: campaign.quantity_discount_type,
                                            unit_price: rule.unit_price,
                                            total_price: rule.total_price,
                                            buy_quantity: rule.buy_quantity,
                                            get_quantity: rule.get_quantity
                                        });
                                        
                                        // Try to determine discount type from rule if campaign.quantity_discount_type is null
                                        const discountType = campaign.quantity_discount_type || 
                                            (rule.unit_price ? 'unit_price' : 
                                             rule.total_price ? 'total_price' : 
                                             (rule.buy_quantity && rule.get_quantity) ? 'buy_x_get_y' : null);
                                        
                                        if (discountType === 'unit_price' && rule.unit_price) {
                                            // Calculate total price: min_quantity * unit_price
                                            const totalPrice = rule.min_quantity * parseFloat(rule.unit_price);
                                            priceDisplay = `${parseFloat(totalPrice).toFixed(0)}₺`;
                                            console.log('✅ Unit price calculated:', totalPrice);
                                        } else if (discountType === 'total_price' && rule.total_price) {
                                            priceDisplay = `${parseFloat(rule.total_price).toFixed(0)}₺`;
                                            console.log('✅ Total price:', rule.total_price);
                                        } else if (discountType === 'buy_x_get_y' && rule.buy_quantity && rule.get_quantity) {
                                            // For buy_x_get_y, calculate price if possible
                                            // If we have unit_price, show: (buy_quantity * unit_price) for buy_quantity+get_quantity items
                                            if (rule.unit_price) {
                                                const totalPrice = rule.buy_quantity * parseFloat(rule.unit_price);
                                                priceDisplay = `${parseFloat(totalPrice).toFixed(0)}₺`;
                                            } else {
                                                priceDisplay = `${rule.buy_quantity}+${rule.get_quantity}`;
                                            }
                                            console.log('✅ Buy X Get Y:', priceDisplay);
                                        } else {
                                            console.log('⚠️ Cannot determine price - discountType:', discountType, 'rule:', rule);
                                        }
                                    } else {
                                        console.log('⚠️ No matching rules for product', product.id);
                                    }
                                } else {
                                    console.log('⚠️ No quantity_rules in campaign');
                                }
                            }

                            return (
                                <div 
                                    key={campaign.id} 
                                    className="flex flex-col gap-0.5"
                                >
                                    <div className="text-xs font-medium flex items-start gap-2" style={{ color: 'var(--campaign-text-color, #92400e)' }}>
                                        <span className="mt-0.5">🎁</span>
                                        <span>
                                            {campaign.title || campaign.campaign_title}
                                            {priceDisplay && (
                                                <span className="font-bold ml-1 text-red-600 text-sm">
                                                    = {typeof priceDisplay === 'string' ? priceDisplay : priceDisplay}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    
                                    {campaign.end_date && (
                                        <div className="ml-6">
                                            <CampaignCountdown endDate={campaign.end_date} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        
                        {/* Show generic campaign messages if needed */}
                        {product.campaign_prices && product.campaign_prices.map((campaignPrice, idx) => {
                            // Only show if we haven't already shown it via campaigns map above
                            const campaignExists = product.campaigns?.some(c => c.id === campaignPrice.campaign_id);
                            if (campaignExists) return null;

                            if (campaignPrice.campaign_type === 'quantity_discount' || campaignPrice.campaign_type === 'combination') {
                                return (
                                    <div 
                                        key={idx} 
                                        className="text-xs text-blue-600 font-medium ml-6"
                                    >
                                        {campaignPrice.campaign_type === 'quantity_discount' && '💡 Adet bazlı indirim mevcut'}
                                        {campaignPrice.campaign_type === 'combination' && '🎯 Kombinasyon kampanyası mevcut'}
                            </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                ) : null}

                {/* Like/Dislike Buttons - No counts, only show selection */}
                {isSettingEnabled('show_likes', true) && (
                    <div className="flex items-center gap-3">
                {/* Like Button */}
                <button
                            onClick={(e) => handleReaction(e, 'like')}
                            disabled={loading}
                            className={`flex items-center justify-center w-10 h-10 rounded-full transition disabled:opacity-50 ${
                                reaction === 'like' ? 'shadow-lg scale-110' : 'hover:scale-105'
                            }`}
                            style={{
                                backgroundColor: reaction === 'like' ? 'var(--like-color, #22c55e)' : 'var(--like-bg-color, #f0fdf4)',
                                color: reaction === 'like' ? '#fff' : 'var(--like-color, #22c55e)'
                            }}
                            title="Beğen"
                        >
                            <svg
                                className="w-5 h-5"
                                fill={reaction === 'like' ? 'currentColor' : 'none'}
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                        </button>
                        
                        {/* Dislike Button */}
                        <button
                            onClick={(e) => handleReaction(e, 'dislike')}
                    disabled={loading}
                            className={`flex items-center justify-center w-10 h-10 rounded-full transition disabled:opacity-50 ${
                                reaction === 'dislike' ? 'shadow-lg scale-110' : 'hover:scale-105'
                            }`}
                            style={{
                                backgroundColor: reaction === 'dislike' ? 'var(--dislike-color, #ef4444)' : 'var(--dislike-bg-color, #fef2f2)',
                                color: reaction === 'dislike' ? '#fff' : 'var(--dislike-color, #ef4444)'
                            }}
                            title="Beğenme"
                >
                    <svg
                                className="w-5 h-5"
                                fill={reaction === 'dislike' ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                    </svg>
                </button>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

export default ProductCard;
